/**
 * [T157] TxByTypeDonutSkin — `tx-by-type-donut` adapter.
 *
 * WidgetFrame-compliant migration from ChartCardShell scaffold.
 * Resolver: categoryBreakdownFacet with groupBy=type.
 *
 * @see https://recharts.org/en-US/api/PieChart
 * @see docs/design/T149-PR5-INSIGHTS-SKINS-SPEC.md §3.1
 */
import { PieChart as PieChartIcon } from 'lucide-react';
import { Cell, Label, Pie, PieChart } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useCurrency } from '@/hooks/useCurrency';
import { WidgetFrame } from '../../WidgetFrame';
import type { SkinProps } from '../types';
import {
  skinTestId,
  SkinLoadingRows,
  SkinEmptyState,
  SkinErrorState,
  SkinLoadingDonut,
  INSIGHTS_PALETTE,
  useSkinFrameProps,
} from './_shared';

const MIN_W = 3;
const MIN_H = 3;

// Income=chart-3, Expense=chart-5, Transfer=chart-1 per spec §3.1
const TYPE_COLOR: Record<string, string> = {
  income: 'hsl(var(--chart-3))',
  expense: 'hsl(var(--chart-5))',
  transfer: 'hsl(var(--chart-1))',
};

export default function TxByTypeDonutSkin(props: SkinProps<'tx-by-type-donut'>) {
  const { widget, data, loading, error } = props;
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;

  const slices = data?.slices ?? [];
  const isEmpty = !loading && !error && slices.length === 0;
  const top = slices[0];

  const config: ChartConfig = slices.reduce(
    (acc, s, i) => {
      acc[s.id || s.name] = {
        label: s.name,
        color: TYPE_COLOR[s.id] ?? INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length],
      };
      return acc;
    },
    {} as ChartConfig,
  );

  const frameProps = useSkinFrameProps(props);

  // Too-small fallback: top-1 type as KPI per spec §3.1
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Transactions by Type'}
        icon={<PieChartIcon className="h-4 w-4" />}
        {...frameProps}
        body={
          <div className="flex flex-col justify-center h-full gap-1 py-1">
            {loading ? (
              <SkinLoadingRows rows={2} />
            ) : top ? (
              <>
                <p className="text-xs font-semibold truncate text-foreground">{top.name}</p>
                <p className="text-lg font-bold truncate">{fmt(top.amount)}</p>
                <p className="text-xs text-muted-foreground">{top.count} txns</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}
          </div>
        }
      />
    );
  }

  // Mobile fallback: 3-row type list per spec §3.1
  if (isMobile) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Transactions by Type'}
        icon={<PieChartIcon className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingRows rows={3} />
          ) : isEmpty ? (
            <div data-testid={skinTestId('tx-by-type-donut', widget.id, 'empty')}>
              <SkinEmptyState
                title="No transactions in this period"
                description="Record transactions to see type breakdown."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <ul className="space-y-1 w-full" role="list" aria-label="Transaction types">
              {slices.slice(0, 3).map((s, i) => (
                <li key={s.id} className="flex items-center gap-2 min-w-0 py-1.5 text-xs">
                  <span
                    className="shrink-0 w-2 h-2 rounded-full"
                    style={{ background: TYPE_COLOR[s.id] ?? INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length] }}
                    aria-hidden="true"
                  />
                  <span className="truncate flex-1 text-foreground">{s.name}</span>
                  <span className="shrink-0 font-medium">{fmt(s.amount)}</span>
                  <span className="shrink-0 text-muted-foreground">{s.percentage.toFixed(1)}%</span>
                </li>
              ))}
            </ul>
          )
        }
      />
    );
  }

  // Main render
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Transactions by Type'}
      icon={<PieChartIcon className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('tx-by-type-donut', widget.id, 'loading')}>
            <SkinLoadingDonut />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('tx-by-type-donut', widget.id, 'error')}>
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          </div>
        ) : isEmpty ? (
          <div data-testid={skinTestId('tx-by-type-donut', widget.id, 'empty')}>
            <SkinEmptyState
              title="No transactions in this period"
              description="Record transactions to see how your activity splits across types."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Transactions by type: ${slices.map((s) => `${s.name} ${s.percentage.toFixed(1)}%`).join(', ')}`}
            className="w-full h-full min-w-0 min-h-0 overflow-hidden"
            data-testid={skinTestId('tx-by-type-donut', widget.id)}
          >
            <ChartContainer config={config} className="h-full w-full overflow-hidden">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Pie
                  data={slices}
                  dataKey="amount"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                  aria-label={slices.map((s) => `${s.name}: ${fmt(s.amount)}, ${s.percentage.toFixed(1)}%`).join(', ')}
                >
                  <Label
                    value={fmt(slices.reduce((acc, s) => acc + s.amount, 0))}
                    position="center"
                    className="fill-foreground text-sm font-semibold"
                    style={{ fontFamily: 'inherit', fontSize: '0.8rem' }}
                  />
                  {slices.map((s, i) => (
                    <Cell
                      key={s.id || `slice-${i}`}
                      fill={TYPE_COLOR[s.id] ?? INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length]}
                      aria-label={`${s.name}: ${fmt(s.amount)}, ${s.percentage.toFixed(1)}%`}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </div>
        )
      }
    />
  );
}
